import React, { useMemo } from 'react';
import { useCurrentFrame, random, interpolate, spring } from 'remotion';

export interface TypeWriterProProps {
  text: string | string[];
  typeSpeed?: number;
  deleteSpeed?: number;
  pauseAfterType?: number;
  pauseAfterDelete?: number;
  errorRate?: number;
  errorCorrectDelay?: number;
  seed?: number | string;
  cursor?: boolean | React.ReactNode;
  blinkSpeed?: number;
  showCursorAfterComplete?: boolean;
  style?: React.CSSProperties;
  className?: string;
  delay?: number;
  loop?: boolean;
  deleteBeforeNext?: boolean;
  syntaxHighlight?: boolean;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
}

interface TimelineEvent {
  frame: number;
  text: string;
}

const DEFAULT_TYPE_SPEED = 3;
const DEFAULT_DELETE_SPEED = 2;
const DEFAULT_PAUSE_TYPE = 30;
const DEFAULT_PAUSE_DELETE = 15;
const KEYBOARD_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,!? ';

export const TypeWriterPro: React.FC<TypeWriterProProps> = ({
  text,
  typeSpeed = DEFAULT_TYPE_SPEED,
  deleteSpeed = DEFAULT_DELETE_SPEED,
  pauseAfterType = DEFAULT_PAUSE_TYPE,
  pauseAfterDelete = DEFAULT_PAUSE_DELETE,
  errorRate = 0,
  errorCorrectDelay = 5,
  seed = 'typewriter',
  cursor = true,
  blinkSpeed = 30,
  showCursorAfterComplete = true,
  style,
  className,
  delay = 0,
  loop = false,
  deleteBeforeNext = true,
  syntaxHighlight = false,
  fontSize = 48,
  fontFamily = 'monospace',
  color = '#00FF00',
}) => {
  const frame = useCurrentFrame();

  const texts = useMemo(() => (Array.isArray(text) ? text : [text]), [text]);

  const { events, totalDuration } = useMemo(() => {
    const timelineEvents: TimelineEvent[] = [{ frame: 0, text: '' }];
    let floatFrame = 0;
    let currentText = '';

    const pushState = () => {
      timelineEvents.push({ frame: Math.round(floatFrame), text: currentText });
    };

    const processString = (str: string, strIndex: number) => {
      const len = str.length;

      for (let i = 0; i < len; i++) {
        const char = str[i];
        const progress = i / Math.max(1, len);

        const charSeed = `${seed}-${strIndex}-${i}`;
        const isError = random(charSeed) < errorRate;

        if (isError) {
          const wrongChar = KEYBOARD_CHARS[Math.floor(random(charSeed + '-wrong') * KEYBOARD_CHARS.length)];

          floatFrame += typeSpeed;
          currentText += wrongChar;
          pushState();

          floatFrame += errorCorrectDelay;
          pushState();

          floatFrame += deleteSpeed;
          currentText = currentText.slice(0, -1);
          pushState();
        }

        floatFrame += typeSpeed;
        currentText += char;
        pushState();
      }

      floatFrame += pauseAfterType;
      pushState();

      if (deleteBeforeNext) {
        const isLast = strIndex === texts.length - 1;
        const shouldDeleteThis = !isLast || loop;

        if (shouldDeleteThis) {
          const currentLen = currentText.length;
          for (let i = currentLen - 1; i >= 0; i--) {
            floatFrame += deleteSpeed;
            currentText = currentText.slice(0, -1);
            pushState();
          }
          floatFrame += pauseAfterDelete;
          pushState();
        }
      } else {
        const isLast = strIndex === texts.length - 1;
        if (isLast && loop) {
          const currentLen = currentText.length;
          for (let i = currentLen - 1; i >= 0; i--) {
            floatFrame += deleteSpeed;
            currentText = currentText.slice(0, -1);
            pushState();
          }
          floatFrame += pauseAfterDelete;
          pushState();
        }
      }
    };

    texts.forEach((str, index) => {
      processString(str, index);
    });

    return { events: timelineEvents, totalDuration: floatFrame };
  }, [
    texts,
    typeSpeed,
    deleteSpeed,
    pauseAfterType,
    pauseAfterDelete,
    errorRate,
    errorCorrectDelay,
    seed,
    loop,
    deleteBeforeNext,
  ]);

  const effectiveStartFrame = delay;
  let relativeFrame = frame - effectiveStartFrame;

  if (loop && totalDuration > 0) {
    relativeFrame = relativeFrame % totalDuration;
    if (relativeFrame < 0) relativeFrame += totalDuration;
  }

  let visibleText = '';
  if (relativeFrame >= 0) {
    let activeIndex = 0;
    for (let i = 0; i < events.length; i++) {
      if (events[i].frame <= relativeFrame) {
        activeIndex = i;
      } else {
        break;
      }
    }
    visibleText = events[activeIndex].text;
  }

  const isComplete = !loop && frame - effectiveStartFrame > totalDuration;
  const shouldShowCursor = cursor && (!isComplete || showCursorAfterComplete) && relativeFrame >= 0;

  let renderCursor = null;
  if (shouldShowCursor) {
    const isBlinking = relativeFrame % blinkSpeed < blinkSpeed / 2;
    const cursorContent = typeof cursor === 'boolean' ? '|' : cursor;

    const cursorStyle: React.CSSProperties = {
      opacity: isBlinking ? 1 : 0,
      display: 'inline-block',
      color: color,
      fontWeight: 'bold',
      textShadow: `0 0 10px ${color}`,
    };

    renderCursor = <span style={cursorStyle}>{cursorContent}</span>;
  }

  // Syntax highlighting (simple implementation)
  const highlightText = (text: string) => {
    if (!syntaxHighlight) return text;

    const keywords = ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while'];
    const parts = text.split(/(\s+)/);

    return parts.map((part, index) => {
      if (keywords.includes(part)) {
        return (
          <span key={index} style={{ color: '#FF79C6' }}>
            {part}
          </span>
        );
      }
      if (part.startsWith('"') || part.startsWith("'")) {
        return (
          <span key={index} style={{ color: '#F1FA8C' }}>
            {part}
          </span>
        );
      }
      if (!isNaN(Number(part))) {
        return (
          <span key={index} style={{ color: '#BD93F9' }}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const containerStyle: React.CSSProperties = {
    ...style,
    fontSize,
    fontFamily,
    color,
    textShadow: `0 0 20px ${color}`,
  };

  return (
    <span style={containerStyle} className={className}>
      {visibleText.split('\n').map((line, i, arr) => (
        <React.Fragment key={i}>
          {syntaxHighlight ? highlightText(line) : line}
          {i < arr.length - 1 && <br />}
        </React.Fragment>
      ))}
      {renderCursor}
    </span>
  );
};
